import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lightbulb, CheckCircle, ArrowRight, Users, TrendingUp, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const QUALIFIER_URL = 'https://mikelmedia-qualify.paperform.co';
const BOOKING_URL = 'https://bookme.name/professionalchameleon/consultation';

const ConsultingPage = () => {
  const { t } = useTranslation();
  const color = '#7D468C';

  const includes_en = [
    '1-on-1 strategy session (60 min)',
    'Full marketing audit of your current channels',
    'Custom 90-day growth roadmap',
    'Competitor analysis & positioning review',
    'Weekly check-ins & accountability calls',
    'Priority email & WhatsApp support',
  ];

  const includes_es = [
    'Sesión estratégica 1-a-1 (60 min)',
    'Auditoría completa de tus canales actuales',
    'Hoja de ruta de crecimiento a 90 días personalizada',
    'Análisis de competidores y revisión de posicionamiento',
    'Llamadas semanales de seguimiento y accountability',
    'Soporte prioritario por email y WhatsApp',
  ];

  const for_en = [
    'Small business owners who feel stuck on marketing',
    'Entrepreneurs launching a brand or product in Miami',
    'Businesses spending money on ads with no clear strategy',
    'Anyone who needs an expert to tell them exactly what to do next',
  ];

  const for_es = [
    'Dueños de negocios que se sienten estancados en su marketing',
    'Emprendedores lanzando una marca o producto en Miami',
    'Negocios que gastan en anuncios sin una estrategia clara',
    'Quienes necesitan un experto que les diga exactamente qué hacer',
  ];

  const results = [
    { stat: '8+', label_en: 'Years of Experience', label_es: 'Años de Experiencia' },
    { stat: '150+', label_en: 'Projects Completed', label_es: 'Proyectos Completados' },
    { stat: '3x', label_en: 'Avg. ROI for Clients', label_es: 'ROI Promedio para Clientes' },
  ];

  const { i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');

  const includes = isES ? includes_es : includes_en;
  const forList = isES ? for_es : for_en;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0F1C2E] flex flex-col">
      <Navbar />

      <main className="flex-1">

        {/* HERO */}
        <section
          className="pt-24 pb-20 px-4"
          style={{ background: `linear-gradient(135deg, ${color}18 0%, ${color}05 100%)` }}
        >
          <div className="max-w-5xl mx-auto">
            <Link
              to="/#servicios"
              className="inline-flex items-center gap-1 text-sm font-semibold mb-8 transition-opacity hover:opacity-70 font-source-sans"
              style={{ color }}
            >
              {t('service_page.back')}
            </Link>

            <div className="flex flex-col md:flex-row md:items-start gap-8">
              {/* Icon */}
              <div
                className="flex-shrink-0 w-20 h-20 flex items-center justify-center"
                style={{ backgroundColor: color + '20' }}
              >
                <Lightbulb size={36} style={{ color }} />
              </div>

              <div>
                {/* Tag */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2.5 h-2.5" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                  <span className="text-xs font-bold tracking-widest uppercase font-poppins" style={{ color }}>
                    {isES ? 'Consultoría' : 'Consulting'}
                  </span>
                </div>

                <h1 className="font-poppins font-bold text-3xl sm:text-4xl lg:text-5xl text-[#233559] dark:text-white leading-tight mb-5">
                  {isES
                    ? 'Tu Estrategia de Marketing, Finalmente Clara'
                    : 'Your Marketing Strategy, Finally Clear'}
                </h1>

                <p className="text-lg text-[#233559]/65 dark:text-white/60 leading-relaxed max-w-2xl font-source-sans">
                  {isES
                    ? 'Deja de adivinar qué funciona. Te damos un plan de acción claro, personalizado para tu negocio en Miami, para que cada dólar que gastes en marketing trabaje para ti.'
                    : "Stop guessing what works. We give you a clear, personalized action plan for your Miami business so every marketing dollar actually works for you."}
                </p>

                {/* Price anchor */}
                <p className="mt-4 text-sm font-semibold font-source-sans" style={{ color }}>
                  {isES ? 'Desde $500 / mes' : 'Starting at $500 / month'}
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-8">
                  <a
                    href={QUALIFIER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3.5 font-poppins font-semibold text-white text-sm tracking-wide transition-opacity hover:opacity-90"
                    style={{ backgroundColor: color }}
                  >
                    {isES ? 'Quiero una estrategia' : 'Get my strategy'}
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-3.5 font-poppins font-semibold text-sm tracking-wide border-2 transition-opacity hover:opacity-80"
                    style={{ color, borderColor: color, backgroundColor: 'transparent' }}
                  >
                    {isES ? 'Reservar consulta' : 'Book a consultation'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RESULTS BAR */}
        <section className="py-12 px-4 bg-[#233559] dark:bg-[#1A2639]">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-around gap-8">
            {results.map((r, i) => (
              <div key={i} className="text-center">
                <div className="font-poppins font-bold text-4xl text-white mb-1">{r.stat}</div>
                <div className="text-white/60 font-source-sans text-sm">{isES ? r.label_es : r.label_en}</div>
              </div>
            ))}
          </div>
        </section>

        {/* WHAT'S INCLUDED + WHO IT'S FOR */}
        <section className="py-20 px-4 bg-white dark:bg-[#0F1C2E]">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.includes_title')}
                </h2>
              </div>
              <ul className="space-y-4">
                {includes.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.for_title')}
                </h2>
              </div>
              <ul className="space-y-4">
                {forList.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Users size={20} className="flex-shrink-0 mt-0.5" style={{ color }} />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 px-4" style={{ backgroundColor: color + '08' }}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-3 h-3" style={{ transform: 'rotate(45deg)', backgroundColor: color }} />
                <span className="text-xs font-bold tracking-widest uppercase font-poppins" style={{ color }}>
                  {isES ? 'El Proceso' : 'The Process'}
                </span>
              </div>
              <h2 className="font-poppins font-bold text-3xl text-[#233559] dark:text-white">
                {isES ? 'Cómo Funciona' : 'How It Works'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  num: '01',
                  en_title: 'Apply',
                  es_title: 'Aplica',
                  en_desc: 'Fill out a short qualifier so we understand your business and goals.',
                  es_desc: 'Completa un breve formulario para que entendamos tu negocio y objetivos.',
                },
                {
                  num: '02',
                  en_title: 'Discovery Call',
                  es_title: 'Llamada de Descubrimiento',
                  en_desc: 'We meet (virtually or in person in Miami) to dive deep into your current situation.',
                  es_desc: 'Nos reunimos (virtual o presencialmente en Miami) para profundizar en tu situación.',
                },
                {
                  num: '03',
                  en_title: 'Get Your Roadmap',
                  es_title: 'Recibe tu Plan',
                  en_desc: 'You receive a custom 90-day marketing roadmap with clear actions and priorities.',
                  es_desc: 'Recibes un plan de marketing a 90 días con acciones claras y prioridades.',
                },
              ].map((step, i) => (
                <div key={i} className="bg-white dark:bg-[#1A2639] p-7 border border-[#233559]/08 dark:border-white/08">
                  <div className="font-poppins font-bold text-4xl mb-4" style={{ color: color + '40' }}>{step.num}</div>
                  <h3 className="font-poppins font-bold text-lg text-[#233559] dark:text-white mb-2">
                    {isES ? step.es_title : step.en_title}
                  </h3>
                  <p className="text-[#233559]/65 dark:text-white/55 font-source-sans text-sm leading-relaxed">
                    {isES ? step.es_desc : step.en_desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className="py-20 px-4" style={{ backgroundColor: color }}>
          <div className="max-w-3xl mx-auto text-center">
            <Star size={32} className="text-white/40 mx-auto mb-6" />
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-white mb-4">
              {isES ? '¿Listo para tener una estrategia que funcione?' : 'Ready for a strategy that actually works?'}
            </h2>
            <p className="text-white/80 text-lg mb-8 font-source-sans">
              {isES
                ? 'Reserva tu consulta directamente o completa el formulario y te contactamos.'
                : 'Book your consultation directly or fill out the form and we\'ll reach out.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white font-poppins font-bold text-sm tracking-wide transition-opacity hover:opacity-90"
                style={{ color }}
              >
                {isES ? 'Reservar consulta' : 'Book a consultation'}
                <ArrowRight size={16} />
              </a>
              <a
                href={QUALIFIER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 font-poppins font-semibold text-sm tracking-wide border-2 border-white/60 text-white transition-opacity hover:opacity-80"
              >
                {isES ? 'Aplicar primero' : 'Apply first'}
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default ConsultingPage;
